import { updateGitHubUsername, getTeamMembers, db, getUserProfile, updateUserProfile } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import MyHeader from "../../components/common/MyHeader";
import { MdCancel } from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import { useLocation, useNavigate } from 'react-router-dom';
import { onSnapshot, doc } from 'firebase/firestore';
import GoBackButton from "./components/GoBackButton";
import GitHubSetupProgress from '../../components/common/GitHubSetupProgress';
import { HomeHeader } from '../Home/HomeHeader';
import {
  PageWrapper, Container, Header, Title, Subtitle,
  Card, MemberRow, FinishWrapper, Footer, FooterText, FooterLink,
} from './components/setupStyles';


function GroupSetupPage() {
  const { user, groupMembers, groupInfo, setGroupMembers } = useAuth();
  const [editState, setEditState] = useState({});
  const [saving, setSaving] = useState(false);
  const [profileName, setProfileName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const isHomeMode =
    location.state?.connectionMode === 'home' ||
    location.state?.settingsHeaderMode === 'home' ||
    !groupInfo?.groupId;
  const displayedMembers = isHomeMode
    ? [{
        email: user?.email || 'current-user',
        name: profileName || user?.displayName || user?.email || 'You',
        github_username: editState[user?.email] ?? ''
      }]
    : groupMembers;

  useEffect(() => {
    if (!isHomeMode || !user?.email) return;

    let isActive = true;

    const loadProfile = async () => {
      try {
        const profile = await getUserProfile(user.email);
        if (!isActive) return;
        setProfileName(profile?.name || user.displayName || user.email);
        setEditState(prev => ({
          ...prev,
          [user.email]: profile?.github || ''
        }));
      } catch (error) {
        console.error('Error loading profile for GitHub setup:', error);
        if (!isActive) return;
        setProfileName(user.displayName || user.email);
      }
    };

    loadProfile();
    return () => {
      isActive = false;
    };
  }, [isHomeMode, user]);

  useEffect(() => {
    if (isHomeMode || !groupInfo?.groupId) return;

    const unsubscribe = onSnapshot(doc(db, "Groups", groupInfo.groupId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const members = data?.members || [];
        setGroupMembers(members); // Sync changes into your state
      }
    });

    return () => unsubscribe(); // Clean up listener
  }, [groupInfo?.groupId, isHomeMode, setGroupMembers]);

  const handleChange = (email, value) => {
    setEditState(prev => ({ ...prev, [email]: value }));
  };

  const handleFinishSetup = async () => {
    try {
      setSaving(true);

      if (isHomeMode) {
        const value = editState[user?.email] !== undefined && editState[user?.email] !== null
          ? String(editState[user.email]).trim()
          : "";
        await updateUserProfile(user.email, { github: value });
        alert("GitHub username updated.");
        return;
      }

      const updates = Object.entries(editState).filter(([_, username]) => username.trim());

      // Case 1: There are updates to save
      if (updates.length > 0) {
        for (const [email, username] of updates) {
          await updateGitHubUsername(groupInfo.groupId, email, username.trim());
        }

        const membersData = await getTeamMembers(user, groupInfo.groupId);
        setGroupMembers(membersData.members);

        const allFilled = membersData.members.every(m => m.github_username);

        if (allFilled) {
          alert("All GitHub usernames updated! Redirecting to repo setup...");
          navigate('/repo-setup');
        } else {
          alert("Usernames updated. Some members still missing GitHub usernames.");
        }

        // Case 2: No updates – check if all usernames are already filled
      } else {
        const allFilled = groupMembers.every(m => m.github_username);

        if (allFilled) {
          alert("All GitHub usernames already set! Redirecting to repo setup...");
          navigate('/repo-setup');
        } else {
          alert("No new usernames entered. Please complete all team members’ GitHub usernames.");
        }
      }

    } catch (err) {
      console.error("Finish setup error:", err);
      alert("Something went wrong while saving.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {isHomeMode ? <HomeHeader text="Connections" link="/student/home" /> : <MyHeader />}

      <PageWrapper>
        <Container>
          <GoBackButton />
          {isHomeMode ? (
            <Header>
              <Title>GitHub Connection</Title>
              <Subtitle>Save your GitHub username to your personal profile.</Subtitle>
            </Header>
          ) : (
            <GitHubSetupProgress
              currentStep={1}
              totalSteps={2}
              labels={['Usernames', 'Repository']}
            />
          )}
          
          {!isHomeMode && (
            <Header>
              <Subtitle>Enter each team member’s GitHub username next to their name.</Subtitle>
            </Header>
          )}
          <Card>
            <CardList>
              {displayedMembers.map((member) => (
                <MemberRow key={member.email}>
                  <CardHeader>
                    <UserName>{member.name}</UserName>
                    <StatusGroup>
                      {member.github_username ? (
                        <>
                          <BaseIcon className='green'><FaCheckCircle /></BaseIcon>
                          <Username>{member.github_username}</Username>
                        </>
                      ) : (
                        <>
                          <BaseIcon className='red'><MdCancel /></BaseIcon>
                          <Status>Missing</Status>
                        </>
                      )}
                    </StatusGroup>
                  </CardHeader>
                  <CardBody>
                    <Input
                      type="text"
                      placeholder="Enter GitHub username"
                      value={editState[member.email] ?? (member.github_username ?? '')}
                      onChange={(e) => handleChange(member.email, e.target.value)}
                    />
                  </CardBody>
                </MemberRow>
              ))}
            </CardList>
            <FinishWrapper>
              <FinishButton onClick={handleFinishSetup} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </FinishButton>
            </FinishWrapper>
          </Card>
          {!isHomeMode && (
            <Footer>
              <FooterText>
                Need help? Check out our{" "}
                <FooterLink
                  href="https://docs.google.com/document/d/10ktlofLuFkFbs9ZEEs2jwpvycE6nz8KBXZyE4FJ8w40/edit?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  documentation
                </FooterLink>
              </FooterText>
            </Footer>
          )}
        </Container>
      </PageWrapper>
    </>
  );
}

export default GroupSetupPage;



const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem; /* space-y-6 */
`;

//Card
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const UserName = styled.span`
  font-size: 1.125rem; /* text-lg */
  font-weight: 600;
  color: #374151; /* gray-700 */
`;

const Status = styled.span`
  color: #ef4444; /* red-500 */
  font-size: 0.875rem; /* text-sm */
  margin-left: 0.25rem;
`;

const CardBody = styled.div`
  display: flex;
  width: 100%;
`;

// Input & Button
const Input = styled.input`
  flex-grow: 1;
  padding: 0.75rem;
  border: 1px solid #d1d5db; /* gray-300 */
  border-radius: 0.375rem;
  outline: none;
  transition: all 0.2s ease-in-out;

  &:focus {
    border-color: transparent;
    box-shadow: 0 0 0 2px #9333ea; /* blue-500 */
  }
`;

const BaseIcon = styled.div`
  font-size: 1.5rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;

  &.red {
    color: #ef4444; /* red-500 */
  }

  &.green {
    color: #22c55e; /* green-500 */
  }
`;

const Username = styled.span`
  color: #15803d;       /* Tailwind's green-700 */
  font-size: 0.875rem;  /* text-sm */
  font-weight: 500;     /* font-medium */
  margin-left: 0.5rem;  /* ml-2 */
`;

const StatusGroup = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0.75rem; /* spacing from UserName */
`;


const FinishButton = styled.button`
  background-color: #9333ea;  
  color: white;
  font-weight: 600;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  transition: background-color 0.15s ease-in-out;

 

  &:hover {
    background-color: #7e22ce;
  }
`;

